import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import allProjectsData from '../data/allProjectsData.js';
import ProjectCard from '../components/ProjectCard';

// Define main categories and sub-categories
const mainCategories = ['All', 'Work In Progress', 'Completed Projects'];
const subCategories = ['All', 'Duplex', 'Apartments', 'Commercial Spaces'];

const ProjectListPage = () => {
    const [selectedMainCategory, setSelectedMainCategory] = useState('All');
    const [selectedSubCategory, setSelectedSubCategory] = useState('All');
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    // Filter projects based on selected categories
    const filteredProjects = allProjectsData.filter(project => {
        // Filter by main category (status)
        const mainCategoryMatch = selectedMainCategory === 'All' ||
            project.status === selectedMainCategory;

        // Handle projects with multiple categories (stored as array)
        const projectCategories = Array.isArray(project.category)
            ? project.category
            : [project.category];

        // Filter by sub-category
        const subCategoryMatch = selectedSubCategory === 'All' ||
            projectCategories.includes(selectedSubCategory);

        return mainCategoryMatch && subCategoryMatch;
    });

    return (
        <section id="projects-page" className="py-20 pt-32 bg-[#eceeF1]">
            <div className="container mx-auto px-4">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h1 className="text-4xl font-bold text-arcdom-navy mb-4">Our Projects</h1>
                    <div className="w-20 h-1 bg-arcdom-gold mx-auto mb-6"></div>
                    <p className="max-w-2xl mx-auto text-gray-600">
                        Explore our complete portfolio, showcasing our expertise across
                        various construction and development domains.
                    </p>
                </motion.div>

                {/* Filter section with custom styling to match your design */}
                <div className="mb-16">
                    {/* Main Category Filter (Work In Progress / Completed Projects) */}
                    <div className="flex flex-wrap justify-center mb-6 gap-2">
                        {mainCategories.map((category, index) => (
                            <button
                                key={index}
                                onClick={() => {
                                    setSelectedMainCategory(category);
                                    // Reset sub-category when changing main category
                                    setSelectedSubCategory('All');
                                }}
                                className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                                    selectedMainCategory === category
                                        ? 'bg-arcdom-navy text-white shadow-md'
                                        : 'bg-white text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>

                    {/* Sub-Category Filter (Duplex, Apartments, Commercial Spaces) */}
                    <div className="flex flex-wrap justify-center gap-2">
                        {subCategories.map((category, index) => (
                            <button
                                key={index}
                                onClick={() => setSelectedSubCategory(category)}
                                className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                                    selectedSubCategory === category
                                        ? 'bg-arcdom-gold text-white shadow-md'
                                        : 'bg-white text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Projects Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredProjects.length > 0 ? (
                        filteredProjects.map((project, index) => (
                            <ProjectCard key={project.id} project={project} index={index} />
                        ))
                    ) : (
                        <div className="col-span-1 md:col-span-2 lg:col-span-3 py-12 text-center">
                            <p className="text-gray-600 text-lg font-medium">
                                No projects found for the selected filters.
                            </p>
                            <button
                                onClick={() => {
                                    setSelectedMainCategory('All');
                                    setSelectedSubCategory('All');
                                }}
                                className="mt-4 bg-arcdom-gold text-white px-6 py-2 rounded-lg hover:bg-arcdom-gold/90 transition-colors"
                            >
                                Show All Projects
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ProjectListPage;
